import { Camera } from '@capacitor/camera';
import { Geolocation } from '@capacitor/geolocation';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Filesystem } from '@capacitor/filesystem';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';

// Dynamic import for Contacts plugin (only available on mobile)
let Contacts = null;
if (Capacitor.isNativePlatform()) {
  import('@capacitor-community/contacts').then((module) => {
    Contacts = module.Contacts;
  }).catch((error) => {
    console.warn('Contacts plugin not available:', error);
  });
}

// 🔐 Enhanced Permissions Manager for Capacitor Apps
export class PermissionsManager {
  static permissions = {
    CAMERA: 'camera',
    PHOTOS: 'photos',
    LOCATION: 'location',
    NOTIFICATIONS: 'notifications',
    MICROPHONE: 'microphone',
    CONTACTS: 'contacts',
    CALENDAR: 'calendar',
    STORAGE: 'storage',
    BLUETOOTH: 'bluetooth',
    BIOMETRIC: 'biometric'
  };

  static permissionStatus = {
    GRANTED: 'granted',
    DENIED: 'denied',
    PROMPT: 'prompt'
  };

  // Check camera permission
  static async checkCameraPermission() {
    try {
      const result = await Camera.checkPermissions();
      return {
        permission: this.permissions.CAMERA,
        status: result.camera,
        canRequest: result.camera === this.permissionStatus.PROMPT
      };
    } catch (error) {
      console.error('❌ Error checking camera permission:', error);
      return { permission: this.permissions.CAMERA, status: 'error' };
    }
  }

  // Request camera permission
  static async requestCameraPermission() {
    try {
      console.log('📷 Requesting camera permission...');
      
      const result = await Camera.requestPermissions();
      
      if (result.camera === this.permissionStatus.GRANTED) {
        console.log('✅ Camera permission granted');
        return { success: true, status: result.camera };
      } else {
        console.log('❌ Camera permission denied');
        return { success: false, status: result.camera };
      }
    } catch (error) {
      console.error('❌ Error requesting camera permission:', error);
      return { success: false, error: error.message };
    }
  }

  // Check photos permission
  static async checkPhotosPermission() {
    try {
      const result = await Camera.checkPermissions();
      return {
        permission: this.permissions.PHOTOS,
        status: result.photos,
        canRequest: result.photos === this.permissionStatus.PROMPT
      };
    } catch (error) {
      console.error('❌ Error checking photos permission:', error);
      return { permission: this.permissions.PHOTOS, status: 'error' };
    }
  }

  // Request photos permission
  static async requestPhotosPermission() {
    try {
      console.log('🖼️ Requesting photos permission...');
      
      const result = await Camera.requestPermissions();
      
      if (result.photos === this.permissionStatus.GRANTED) {
        console.log('✅ Photos permission granted');
        return { success: true, status: result.photos };
      } else {
        console.log('❌ Photos permission denied');
        return { success: false, status: result.photos };
      }
    } catch (error) {
      console.error('❌ Error requesting photos permission:', error);
      return { success: false, error: error.message };
    }
  }

  // Check location permission
  static async checkLocationPermission() {
    try {
      const result = await Geolocation.checkPermissions();
      return {
        permission: this.permissions.LOCATION,
        status: result.location,
        canRequest: result.location === this.permissionStatus.PROMPT
      };
    } catch (error) {
      console.error('❌ Error checking location permission:', error);
      return { permission: this.permissions.LOCATION, status: 'error' };
    }
  }

  // Request location permission
  static async requestLocationPermission() {
    try {
      console.log('📍 Requesting location permission...');
      
      const result = await Geolocation.requestPermissions();
      
      if (result.location === this.permissionStatus.GRANTED) {
        console.log('✅ Location permission granted');
        return { success: true, status: result.location };
      } else {
        console.log('❌ Location permission denied');
        return { success: false, status: result.location };
      }
    } catch (error) {
      console.error('❌ Error requesting location permission:', error);
      return { success: false, error: error.message };
    }
  }

  // Check notification permission
  static async checkNotificationPermission() {
    try {
      const result = await LocalNotifications.checkPermissions();
      return {
        permission: this.permissions.NOTIFICATIONS,
        status: result.display,
        canRequest: result.display === this.permissionStatus.PROMPT
      };
    } catch (error) {
      console.error('❌ Error checking notification permission:', error);
      return { permission: this.permissions.NOTIFICATIONS, status: 'error' };
    }
  }

  // Request notification permission
  static async requestNotificationPermission() {
    try {
      console.log('🔔 Requesting notification permission...');
      
      const result = await LocalNotifications.requestPermissions();
      
      if (result.display === this.permissionStatus.GRANTED) {
        console.log('✅ Notification permission granted');
        return { success: true, status: result.display };
      } else {
        console.log('❌ Notification permission denied');
        return { success: false, status: result.display };
      }
    } catch (error) {
      console.error('❌ Error requesting notification permission:', error);
      return { success: false, error: error.message };
    }
  }

  // Check microphone permission (web-based)
  static async checkMicrophonePermission() {
    try {
      if (!Capacitor.isNativePlatform()) {
        return {
          permission: this.permissions.MICROPHONE,
          status: 'granted',
          canRequest: false
        };
      }

      // For native platforms, assume microphone is available
      return {
        permission: this.permissions.MICROPHONE,
        status: 'granted',
        canRequest: false
      };
    } catch (error) {
      console.error('❌ Error checking microphone permission:', error);
      return { permission: this.permissions.MICROPHONE, status: 'error' };
    }
  }

  // Request microphone permission (web-based)
  static async requestMicrophonePermission() {
    try {
      console.log('🎤 Requesting microphone permission...');
      
      if (!Capacitor.isNativePlatform()) {
        console.log('✅ Microphone permission granted (web)');
        return { success: true, status: 'granted' };
      }

      // For native platforms, assume microphone is available
      console.log('✅ Microphone permission granted');
      return { success: true, status: 'granted' };
    } catch (error) {
      console.error('❌ Error requesting microphone permission:', error);
      return { success: false, error: error.message };
    }
  }

  // Check contacts permission
  static async checkContactsPermission() {
    try {
      if (!Capacitor.isNativePlatform()) {
        return {
          permission: this.permissions.CONTACTS,
          status: 'denied',
          canRequest: false,
          message: 'Contacts are only available on mobile devices'
        };
      }

      if (!Contacts) {
        console.warn('⚠️ Contacts plugin not loaded yet');
        return {
          permission: this.permissions.CONTACTS,
          status: 'prompt',
          canRequest: true
        };
      }

      const result = await Contacts.checkPermissions();
      return {
        permission: this.permissions.CONTACTS,
        status: result.contacts,
        canRequest: result.contacts === this.permissionStatus.PROMPT
      };
    } catch (error) {
      console.error('❌ Error checking contacts permission:', error);
      return { permission: this.permissions.CONTACTS, status: 'error' };
    }
  }

  // Request contacts permission
  static async requestContactsPermission() {
    try {
      console.log('📞 Requesting contacts permission...');
      
      if (!Capacitor.isNativePlatform()) {
        console.log('❌ Contacts not available on web');
        return { success: false, status: 'denied', message: 'Contacts are only available on mobile devices' };
      }

      if (!Contacts) {
        console.error('❌ Contacts plugin not available');
        return { success: false, status: 'denied', message: 'Contacts plugin not loaded' };
      }

      const result = await Contacts.requestPermissions();
      
      if (result.contacts === this.permissionStatus.GRANTED) {
        console.log('✅ Contacts permission granted');
        return { success: true, status: result.contacts };
      } else {
        console.log('❌ Contacts permission denied');
        return { success: false, status: result.contacts };
      }
    } catch (error) {
      console.error('❌ Error requesting contacts permission:', error);
      return { success: false, error: error.message };
    }
  }

  // Check calendar permission (web-based)
  static async checkCalendarPermission() {
    try {
      if (!Capacitor.isNativePlatform()) {
        return {
          permission: this.permissions.CALENDAR,
          status: 'granted',
          canRequest: false
        };
      }

      // For native platforms, assume calendar is available
      return {
        permission: this.permissions.CALENDAR,
        status: 'granted',
        canRequest: false
      };
    } catch (error) {
      console.error('❌ Error checking calendar permission:', error);
      return { permission: this.permissions.CALENDAR, status: 'error' };
    }
  }

  // Request calendar permission (web-based)
  static async requestCalendarPermission() {
    try {
      console.log('📅 Requesting calendar permission...');
      
      if (!Capacitor.isNativePlatform()) {
        console.log('✅ Calendar permission granted (web)');
        return { success: true, status: 'granted' };
      }

      // For native platforms, assume calendar is available
      console.log('✅ Calendar permission granted');
      return { success: true, status: 'granted' };
    } catch (error) {
      console.error('❌ Error requesting calendar permission:', error);
      return { success: false, error: error.message };
    }
  }

  // Check storage permission
  static async checkStoragePermission() {
    try {
      if (!Capacitor.isNativePlatform()) {
        return {
          permission: this.permissions.STORAGE,
          status: 'granted',
          canRequest: false
        };
      }

      const result = await Filesystem.checkPermissions();
      return {
        permission: this.permissions.STORAGE,
        status: result.publicStorage,
        canRequest: result.publicStorage === this.permissionStatus.PROMPT
      };
    } catch (error) {
      console.error('❌ Error checking storage permission:', error);
      return { permission: this.permissions.STORAGE, status: 'error' };
    }
  }

  // Request storage permission
  static async requestStoragePermission() {
    try {
      console.log('💾 Requesting storage permission...');
      
      if (!Capacitor.isNativePlatform()) {
        console.log('✅ Storage permission granted (web)');
        return { success: true, status: 'granted' };
      }

      const result = await Filesystem.requestPermissions();
      
      if (result.publicStorage === this.permissionStatus.GRANTED) {
        console.log('✅ Storage permission granted');
        return { success: true, status: result.publicStorage };
      } else {
        console.log('❌ Storage permission denied');
        return { success: false, status: result.publicStorage };
      }
    } catch (error) {
      console.error('❌ Error requesting storage permission:', error);
      return { success: false, error: error.message };
    }
  }

  // Check bluetooth permission (web-based)
  static async checkBluetoothPermission() {
    try {
      if (!Capacitor.isNativePlatform()) {
        return {
          permission: this.permissions.BLUETOOTH,
          status: 'granted',
          canRequest: false
        };
      }

      // For native platforms, assume bluetooth is available
      return {
        permission: this.permissions.BLUETOOTH,
        status: 'granted',
        canRequest: false
      };
    } catch (error) {
      console.error('❌ Error checking bluetooth permission:', error);
      return { permission: this.permissions.BLUETOOTH, status: 'error' };
    }
  }

  // Request bluetooth permission (web-based)
  static async requestBluetoothPermission() {
    try {
      console.log('🔵 Requesting bluetooth permission...');
      
      if (!Capacitor.isNativePlatform()) {
        console.log('✅ Bluetooth permission granted (web)');
        return { success: true, status: 'granted' };
      }

      // For native platforms, assume bluetooth is available
      console.log('✅ Bluetooth permission granted');
      return { success: true, status: 'granted' };
    } catch (error) {
      console.error('❌ Error requesting bluetooth permission:', error);
      return { success: false, error: error.message };
    }
  }

  // Request all essential permissions
  static async requestEssentialPermissions() {
    try {
      console.log('🔐 Requesting essential permissions...');
      
      const results = await Promise.allSettled([
        this.requestCameraPermission(),
        this.requestPhotosPermission(),
        this.requestNotificationPermission(),
        this.requestStoragePermission()
      ]);

      const successCount = results.filter(r => r.status === 'fulfilled' && r.value.success).length;
      console.log(`✅ ${successCount}/${results.length} essential permissions granted`);

      return {
        success: successCount > 0,
        results: results.map(r => r.status === 'fulfilled' ? r.value : { success: false, error: 'Failed' })
      };
    } catch (error) {
      console.error('❌ Error requesting essential permissions:', error);
      return { success: false, error: error.message };
    }
  }

  // Request all permissions
  static async requestAllPermissions() {
    try {
      console.log('🔐 Requesting all permissions...');
      
      const results = await Promise.allSettled([
        this.requestCameraPermission(),
        this.requestPhotosPermission(),
        this.requestLocationPermission(),
        this.requestNotificationPermission(),
        this.requestMicrophonePermission(),
        this.requestContactsPermission(),
        this.requestCalendarPermission(),
        this.requestStoragePermission(),
        this.requestBluetoothPermission()
      ]);

      const successCount = results.filter(r => r.status === 'fulfilled' && r.value.success).length;
      console.log(`✅ ${successCount}/${results.length} permissions granted`);

      return {
        success: successCount > 0,
        results: results.map(r => r.status === 'fulfilled' ? r.value : { success: false, error: 'Failed' })
      };
    } catch (error) {
      console.error('❌ Error requesting all permissions:', error);
      return { success: false, error: error.message };
    }
  }

  // Get permission status for a specific permission
  static async getPermissionStatus(permission) {
    switch (permission) {
      case this.permissions.CAMERA:
        return await this.checkCameraPermission();
      case this.permissions.PHOTOS:
        return await this.checkPhotosPermission();
      case this.permissions.LOCATION:
        return await this.checkLocationPermission();
      case this.permissions.NOTIFICATIONS:
        return await this.checkNotificationPermission();
      case this.permissions.MICROPHONE:
        return await this.checkMicrophonePermission();
      case this.permissions.CONTACTS:
        return await this.checkContactsPermission();
      case this.permissions.CALENDAR:
        return await this.checkCalendarPermission();
      case this.permissions.STORAGE:
        return await this.checkStoragePermission();
      case this.permissions.BLUETOOTH:
        return await this.checkBluetoothPermission();
      default:
        return { permission, status: 'unknown', canRequest: false };
    }
  }

  // Open app settings
  static async openAppSettings() {
    try {
      if (!Capacitor.isNativePlatform()) {
        console.log('📱 App settings not available on web');
        return { 
          success: false, 
          message: 'App settings not available on web. Please check your browser settings manually.' 
        };
      }

      console.log('📱 Opening app settings...');
      
      // Use the App plugin to open native settings
      const { openSettings } = await App.getInfo();
      await App.openSettings();
      
      console.log('✅ App settings opened');
      return { success: true, message: 'App settings opened' };
    } catch (error) {
      console.error('❌ Error opening app settings:', error);
      
      // Fallback: Provide instructions if direct opening fails
      const platform = Capacitor.getPlatform();
      const instructions = platform === 'android' 
        ? 'Please go to Settings > Apps > ACCTOO > Permissions'
        : 'Please go to Settings > ACCTOO > Permissions';
      
      return { 
        success: false, 
        error: error.message,
        message: `Unable to open settings automatically. ${instructions}` 
      };
    }
  }
}

export default PermissionsManager;